import { Component, signal, effect, computed, inject, HostListener } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { RouteProgressComponent } from '../../ui/route-progress/route-progress.component';
import { SnackbarComponent } from '../../ui/snackbar/snackbar.component';
import { IdleWarningComponent } from '../idle-warning/idle-warning.component';
import { IdleService } from '../../../../core/services/idle.service';
// Eagerly initialise the route-loading service so it subscribes to router events
import { RouteLoadingService } from '../../../../core/services/route-loading.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, TopbarComponent, RouteProgressComponent, SnackbarComponent, IdleWarningComponent],
  styles: [`
    .shell-sidebar {
      position: fixed;
      top: 0; left: 0; bottom: 0;
      z-index: 40;
      transition: width 0.25s cubic-bezier(0.4,0,0.2,1), transform 0.25s cubic-bezier(0.4,0,0.2,1);
    }
    .shell-topbar {
      position: fixed;
      top: 0; right: 0;
      height: 60px;
      z-index: 30;
      transition: left 0.25s cubic-bezier(0.4,0,0.2,1);
    }
    .shell-main {
      transition: margin-left 0.25s cubic-bezier(0.4,0,0.2,1);
      margin-top: 60px;
      min-height: calc(100vh - 60px);
    }
    .mobile-backdrop {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.6);
      z-index: 35;
      animation: fadeIn 0.2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  `],
  template: `
    <!-- Route navigation progress bar -->
    <app-route-progress />

    <!-- Toast snackbar container -->
    <app-snackbar />

    <!-- Inactivity auto-logout prompt -->
    <app-idle-warning />

    <!-- Mobile backdrop -->
    @if (isMobile() && mobileOpen()) {
      <div class="mobile-backdrop" (click)="mobileOpen.set(false)"></div>
    }

    <!-- Sidebar -->
    <app-sidebar
      class="shell-sidebar"
      [style.width]="desktopSidebarWidth()"
      [style.transform]="isMobile() ? (mobileOpen() ? 'translateX(0)' : 'translateX(-240px)') : 'translateX(0)'"
      [collapsed]="isMobile() ? false : !sidebarHovered()"
      (hoveredChange)="sidebarHovered.set($event)"
      (toggleCollapsed)="onSidebarToggle()" />

    <!-- Topbar -->
    <app-topbar
      class="shell-topbar"
      [style.left]="isMobile() ? '0' : desktopSidebarWidth()"
      [title]="pageTitle()"
      [showMenuBtn]="isMobile()"
      (menuToggled)="toggleMobileOpen()" />

    <!-- Main content -->
    <main
      class="shell-main px-3 py-4 sm:p-6 overflow-y-auto"
      [style.margin-left]="isMobile() ? '0' : desktopSidebarWidth()">
      <router-outlet />
    </main>
  `,
})
export class AppShellComponent {
  private router = inject(Router);
  // Eagerly subscribe to router events for the progress bar
  readonly routeLoading = inject(RouteLoadingService);
  // Instantiate the inactivity tracker (starts/stops with the auth state)
  private idle = inject(IdleService);

  private readonly ROUTE_TITLES: Record<string, string> = {
    '/dashboard':    'Dashboard',
    '/destinations': 'Destinations',
    '/categories':   'Categories',
    '/jobs/new':     'New Job',
    '/settings':     'Settings',
  };

  pageTitle = signal('SAD Sentiment');
  sidebarCollapsed = signal(true);
  sidebarHovered = signal(false);
  mobileOpen = signal(false);
  isMobile = signal(window.innerWidth < 768);

  /** Desktop: flat icons by default, full nav on hover. Mobile: always full width. */
  desktopSidebarWidth = computed(() => {
    if (this.isMobile()) return '240px';
    return this.sidebarHovered() ? '240px' : '60px';
  });

  constructor() {
    effect(() => {
      localStorage.setItem('sidebar_collapsed', String(this.sidebarCollapsed()));
    });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const seg = '/' + e.urlAfterRedirects.split('/')[1].split('?')[0];
        this.pageTitle.set(this.ROUTE_TITLES[seg] ?? 'SAD Sentiment');
        this.mobileOpen.set(false); // close mobile drawer on navigate
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile.set(window.innerWidth < 768);
    if (!this.isMobile()) this.mobileOpen.set(false);
  }

  toggleMobileOpen(): void {
    this.mobileOpen.update(v => !v);
  }

  onSidebarToggle(): void {
    if (this.isMobile()) {
      this.mobileOpen.set(false);
    } else {
      this.sidebarCollapsed.update(v => !v);
    }
  }
}
